import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
} from "react";
import useProfile from "../utils/useProfile";

export type Locale = "id" | "en" | "nl" | "cn" | "fr" | "de";

interface LocaleContextType {
    locale: Locale;
    setLocale: (locale: Locale) => void;
    t: (key: string, params?: Record<string, string | number>) => string;
    isLoading: boolean;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

interface LocaleProviderProps {
    children: ReactNode;
}

// Cache untuk menyimpan terjemahan yang sudah dimuat
const translationCache: Record<Locale, any> = {} as Record<Locale, any>;

// Fungsi untuk membaca query string dan mendapatkan locale
const getLocaleFromQueryString = (): Locale | null => {
    if (typeof window === "undefined") return null;

    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get("lang");

    if (langParam && ["id", "en", "nl", "cn", "fr", "de"].includes(langParam)) {
        return langParam as Locale;
    }

    return null;
};

// Fungsi untuk memuat terjemahan secara lazy
const loadTranslations = async (locale: Locale): Promise<any> => {
    // Jika sudah ada di cache, return dari cache
    if (translationCache[locale]) {
        return translationCache[locale];
    }

    try {
        // Dynamic import untuk memuat file JSON
        const translations = await import(`../locales/${locale}.json`);
        translationCache[locale] = translations.default;
        return translations.default;
    } catch (error) {
        console.error(`Failed to load translations for ${locale}:`, error);
        // Fallback ke bahasa Inggris jika gagal
        if (locale !== "en") {
            return await loadTranslations("en");
        }
        throw error;
    }
};

export const LocaleProvider: React.FC<LocaleProviderProps> = ({ children }) => {
    const [locale, setLocaleState] = useState<Locale>("en"); // Default to English per requirement
    const [translations, setTranslations] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const hasSavedLocale =
        typeof window !== "undefined" && !!localStorage.getItem("locale");
    const { profile } = useProfile(
        typeof window !== "undefined" ? window.location.search : "",
        hasSavedLocale
    );

    const supportedLocales: Locale[] = ["id", "en", "nl", "cn", "fr", "de"];

    const mapCountryCodeToLocale = (countryCode?: string): Locale => {
        const cc = (countryCode || "").toUpperCase();
        // Basic mapping; extend as needed
        switch (cc) {
            case "ID":
                return "id";
            case "NL":
                return "nl";
            case "CN":
            case "HK":
            case "TW":
                return "cn";
            case "FR":
                return "fr";
            case "DE":
                return "de";
            default:
                return "en";
        }
    };

    // Load locale from query string or localStorage on mount
    useEffect(() => {
        // Prioritas: Query string > localStorage > profile country_code > default (en)
        const queryLocale = getLocaleFromQueryString();

        if (queryLocale) {
            setLocaleState(queryLocale);
            if (typeof window !== "undefined") {
                localStorage.setItem("locale", queryLocale);
            }
            return;
        }

        const savedLocale = (
            typeof window !== "undefined"
                ? (localStorage.getItem("locale") as Locale | null)
                : null
        ) as Locale | null;

        if (savedLocale && supportedLocales.includes(savedLocale)) {
            setLocaleState(savedLocale);
            return;
        }

        // Jika belum ada locale, coba dari profile
        if (profile?.church?.country_code) {
            const derived = mapCountryCodeToLocale(profile.church.country_code);
            setLocaleState(derived);
            if (typeof window !== "undefined") {
                localStorage.setItem("locale", derived);
            }
        }
        // Jika profile belum tersedia, biarkan default (en)
    }, [profile]);

    // Load translations when locale changes
    useEffect(() => {
        const loadTranslationsForLocale = async () => {
            setIsLoading(true);
            try {
                const loadedTranslations = await loadTranslations(locale);
                setTranslations(loadedTranslations);
            } catch (error) {
                console.error("Failed to load translations:", error);
                // Fallback ke bahasa Inggris
                if (locale !== "en") {
                    const fallbackTranslations = await loadTranslations("en");
                    setTranslations(fallbackTranslations);
                }
            } finally {
                setIsLoading(false);
            }
        };

        loadTranslationsForLocale();
    }, [locale]);

    const setLocale = (newLocale: Locale) => {
        setLocaleState(newLocale);
        localStorage.setItem("locale", newLocale);

        // Hapus query string lang dari URL setelah locale diubah
        if (typeof window !== "undefined") {
            const url = new URL(window.location.href);
            url.searchParams.delete("lang");
            window.history.replaceState({}, "", url.toString());
        }
    };

    const t = (
        key: string,
        params?: Record<string, string | number>
    ): string => {
        // Jika translations belum dimuat, return key
        if (!translations) {
            return key;
        }

        const keys = key.split(".");
        let value: any = translations;

        for (const k of keys) {
            if (value && typeof value === "object" && k in value) {
                value = value[k];
            } else {
                // Fallback ke bahasa Inggris jika key tidak ditemukan
                if (locale !== "en" && translationCache["en"]) {
                    value = translationCache["en"];
                    for (const fallbackKey of keys) {
                        if (
                            value &&
                            typeof value === "object" &&
                            fallbackKey in value
                        ) {
                            value = value[fallbackKey];
                        } else {
                            return key; // Return key jika tidak ditemukan di mana pun
                        }
                    }
                } else {
                    return key; // Return key jika tidak ditemukan
                }
                break;
            }
        }

        if (typeof value !== "string") {
            return key;
        }

        // Replace parameters in the string
        if (params) {
            return value.replace(/\{\{(\w+)\}\}/g, (match, paramKey) => {
                return params[paramKey]?.toString() || match;
            });
        }

        return value;
    };

    return (
        <LocaleContext.Provider value={{ locale, setLocale, t, isLoading }}>
            {children}
        </LocaleContext.Provider>
    );
};

export const useLocale = (): LocaleContextType => {
    const context = useContext(LocaleContext);
    if (context === undefined) {
        throw new Error("useLocale must be used within a LocaleProvider");
    }
    return context;
};

// Hook yang aman untuk digunakan di komponen yang mungkin belum memiliki provider
export const useSafeLocale = (): LocaleContextType | null => {
    const context = useContext(LocaleContext);
    return context || null;
};
